import { useState } from 'react';
import { Form, Input, Button, Card, message } from 'antd';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../store';
import { login, requestOtp } from '../store/slices/authSlice';
import { PhoneInput } from '../components/ui/PhoneInput';

export function LoginPage() {
  const [step, setStep] = useState<'phone' | 'code'>('phone');
  const [phone, setPhone] = useState('');
  const [otpCode, setOtpCode] = useState('');
  const dispatch = useDispatch<AppDispatch>();
  const { isLoading, error } = useSelector((state: RootState) => state.auth);

  const handleRequestOtp = async () => {
    if (!phone) {
      message.error('Veuillez saisir votre numéro de téléphone');
      return;
    }

    // Construire le numéro complet avec l'indicatif tunisien
    const fullPhoneNumber = `+216${phone}`;

    try {
      const response = await dispatch(requestOtp({ phone: fullPhoneNumber })).unwrap();
      console.log('✅ OTP Response:', response);
      
      // Remplir automatiquement le code OTP si disponible
      if (response?.otp) {
        console.log('🔑 Auto-filling OTP:', response.otp);
        setOtpCode(response.otp);
      }
      
      setStep('code');
      message.success('Code OTP envoyé');
    } catch (error) {
      message.error('Erreur lors de l\'envoi du code');
    }
  };

  const handleVerifyCode = async (values: { code: string }) => {
    try {
      // Utiliser le numéro complet avec l'indicatif pour la connexion
      const fullPhoneNumber = `+216${phone}`;
      await dispatch(login({ phone: fullPhoneNumber, code: values.code })).unwrap();
      message.success('Connexion réussie');
    } catch (error) {
      message.error('Code invalide');
    }
  };

  const handleBackToPhone = () => {
    setStep('phone');
    setOtpCode('');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 to-secondary-50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md shadow-lg">
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-primary-500 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-white text-2xl font-bold">P</span>
          </div>
          <h1 className="text-2xl font-bold text-neutral-900 mb-2">
            Tunisie Parking Admin
          </h1>
          <p className="text-neutral-600">
            {step === 'phone' 
              ? 'Connexion pour les administrateurs'
              : 'Entrez le code de vérification'
            }
          </p>
        </div>

        {step === 'phone' ? (
          <div className="space-y-4">
            <Form.Item label="Numéro de téléphone" required>
              <PhoneInput
                placeholder="23282363"
                value={phone}
                onChange={setPhone}
              />
            </Form.Item>
            
            <Button
              type="primary"
              size="large"
              onClick={handleRequestOtp}
              loading={isLoading}
              disabled={!phone}
              className="w-full"
            >
              Envoyer le code
            </Button>
          </div>
        ) : (
          <Form onFinish={handleVerifyCode} layout="vertical" initialValues={{ code: otpCode }}>
            <Form.Item
              name="code"
              label="Code de vérification"
              rules={[{ required: true, message: 'Veuillez saisir le code' }]}
            >
              <Input
                placeholder="123456"
                size="large"
                maxLength={6}
                value={otpCode}
                onChange={(e) => setOtpCode(e.target.value)}
              />
            </Form.Item>
            
            <div className="space-y-3">
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                loading={isLoading}
                className="w-full"
              >
                Vérifier le code
              </Button>
              
              <Button
                type="link"
                onClick={handleBackToPhone}
                className="w-full"
              >
                Changer de numéro
              </Button>
            </div>
          </Form>
        )}

        {error && (
          <div className="mt-4 p-3 bg-error-50 border border-error-200 rounded-lg">
            <p className="text-error-600 text-sm">{error}</p>
          </div>
        )}
      </Card>
    </div>
  );
}
